import { Link, useLocation } from "wouter";
import { ArrowRight, Plus } from "lucide-react";
import { Button, ButtonProps } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useLanguage } from "@/components/language-provider";
import { Mascot } from "@/components/assistant/mascot";
import { limitReason, planName, quotaResetLabel, useDocumentLimit } from "@/lib/document-limit";

export const PLANS_ROUTE = "/settings?action=plans";

interface NewDocumentButtonProps extends Omit<ButtonProps, "onClick"> {
  /** Where the button goes when the quota allows (default: the wizard). */
  href?: string;
  /** Called instead of navigating (dashboard keeps its own check). */
  onClick?: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}

/**
 * "New document" entry point that locks itself when the monthly quota is used up: the button is disabled and the
 * hover/tap tooltip says why and when it resets. The wizard and the API refuse too; this just keeps people from
 * walking into a dead end.
 */
export function NewDocumentButton({ href = "/documents/new", onClick, icon, children, testId, ...rest }: NewDocumentButtonProps) {
  const { language } = useLanguage();
  const { data, reached } = useDocumentLimit();
  const [, navigate] = useLocation();

  if (reached && data) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span tabIndex={0} className="inline-flex" data-testid={testId ? `${testId}-locked` : undefined}>
            <Button {...rest} disabled aria-disabled="true" className={`${rest.className ?? ""} pointer-events-none`} data-testid={testId}>
              {icon ?? <Plus className="mr-2 h-4 w-4" />}
              {children}
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-xs text-sm" data-testid="new-document-limit-tooltip">
          {limitReason(data, language)}
          <button type="button" className="mt-1 block font-medium underline" onClick={() => navigate(PLANS_ROUTE)}>
            {language === "pt" ? "Ver planos" : "See plans"}
          </button>
        </TooltipContent>
      </Tooltip>
    );
  }

  if (onClick) {
    return (
      <Button {...rest} onClick={onClick} data-testid={testId}>
        {icon ?? <Plus className="mr-2 h-4 w-4" />}
        {children}
      </Button>
    );
  }
  return (
    <Link href={href}>
      <Button {...rest} data-testid={testId}>
        {icon ?? <Plus className="mr-2 h-4 w-4" />}
        {children}
      </Button>
    </Link>
  );
}

/** Banner for the dashboard and the documents list once the quota is used up (or on the last document). */
export function DocumentLimitBanner({ className = "" }: { className?: string }) {
  const { language } = useLanguage();
  const { data, reached, lastOne } = useDocumentLimit();
  if (!data || (!reached && !lastOne)) return null;
  const pt = language === "pt";
  const reset = quotaResetLabel(language);
  return (
    <div
      className={`flex items-center gap-4 rounded-xl border p-4 ${reached ? "border-amber-300 bg-amber-50 dark:border-amber-700/60 dark:bg-amber-950/30" : "border-primary/40 bg-primary/5"} ${className}`}
      data-testid={reached ? "document-limit-banner" : "document-limit-last-banner"}
    >
      <Mascot mood={reached ? "sorry" : "wave"} size={56} />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {reached
            ? (pt ? `Você usou os ${data.limit} documentos do mês no plano ${planName(data.plan)}` : `You used this month's ${data.limit} documents on the ${planName(data.plan)} plan`)
            : (pt ? `Este é o seu último documento do mês (${data.currentCount + 1}/${data.limit})` : `This is your last document this month (${data.currentCount + 1}/${data.limit})`)}
        </p>
        <p className="text-sm text-muted-foreground">
          {reached
            ? (pt ? `Libera de novo em ${reset}. Para continuar agora, escolha um plano: a partir de 100 documentos por mês.` : `It resets on ${reset}. To keep going now, pick a plan: from 100 documents a month.`)
            : (pt ? `Depois dele, só em ${reset} ou com um plano maior.` : `After this one, the next is on ${reset} or on a bigger plan.`)}
        </p>
      </div>
      <Link href={PLANS_ROUTE}>
        <Button size="sm" data-testid="document-limit-see-plans">
          {pt ? "Ver planos" : "See plans"} <ArrowRight className="ml-1 h-4 w-4" />
        </Button>
      </Link>
    </div>
  );
}

/** Full-screen replacement of the wizard when nothing more can be sent this month. */
export function DocumentLimitReached({ onBack }: { onBack: () => void }) {
  const { language } = useLanguage();
  const { data } = useDocumentLimit();
  const pt = language === "pt";
  const reset = quotaResetLabel(language);
  return (
    <div className="mx-auto max-w-xl rounded-2xl border bg-card p-8 text-center shadow-sm" data-testid="wizard-limit-reached">
      <div className="flex justify-center"><Mascot mood="sorry" size={120} /></div>
      <h2 className="mt-4 text-2xl font-semibold">
        {pt ? "Opa, os documentos do mês acabaram" : "Oops, this month's documents are used up"}
      </h2>
      <p className="mt-2 text-muted-foreground">
        {data
          ? (pt
            ? `O plano ${planName(data.plan)} tem ${data.limit} documentos por mês e você já usou ${Math.min(data.currentCount, data.limit)}. Libera de novo em ${reset}.`
            : `The ${planName(data.plan)} plan has ${data.limit} documents a month and you already used ${Math.min(data.currentCount, data.limit)}. It resets on ${reset}.`)
          : (pt ? `Libera de novo em ${reset}.` : `It resets on ${reset}.`)}
      </p>
      <p className="mt-1 text-sm text-muted-foreground">
        {pt ? "Quer continuar hoje? Os planos pagos começam em 100 documentos por mês." : "Want to keep going today? Paid plans start at 100 documents a month."}
      </p>
      <div className="mt-6 flex flex-wrap justify-center gap-3">
        <Link href={PLANS_ROUTE}>
          <Button data-testid="wizard-limit-see-plans">{pt ? "Ver planos" : "See plans"} <ArrowRight className="ml-1 h-4 w-4" /></Button>
        </Link>
        <Button variant="outline" onClick={onBack} data-testid="wizard-limit-back">{pt ? "Voltar para Documentos" : "Back to Documents"}</Button>
      </div>
    </div>
  );
}
